<template>
  <v-dialog
    :model-value="modelValue"
    @update:model-value="$emit('update:modelValue', $event)"
    width="640"
    content-class="card text-center"
  >
    <v-btn
      icon
      dark
      variant="text"
      class="dialog-close-btn"
      @click="$emit('close-dialog')"
    >
      <v-icon>mdi-close</v-icon>
    </v-btn>

    <h2>Link delen met de specialist</h2>
    <p>
      Kies de vestiging waar de specialist staat. De link toont alleen de
      aanmeldingen van deze activiteit op die locatie, en vervalt vanzelf zodra
      de laatste datum voorbij is.
    </p>
    <k-spacing y="6"></k-spacing>

    <v-select
      v-model="locationId"
      :items="locations"
      item-title="name"
      item-value="id"
      variant="solo"
      placeholder="Kies een vestiging"
      :loading="loadingLocations"
      :disabled="loadingLocations || !locations.length"
      hide-details
    />
    <!-- Een uitgeschakelde keuzelijst zonder uitleg leest als een storing. -->
    <p
      v-if="!loadingLocations && !locations.length"
      class="text-neutral-600 mt-2"
    >
      Deze activiteit staat nog op geen enkele vestiging gepland. Zet er eerst
      een specialist op een locatie; daarna is er een link te delen.
    </p>
    <k-spacing y="4"></k-spacing>

    <div v-if="!link">
      <v-btn
        class="btn text-white"
        color="var(--tertiary)"
        :loading="creating"
        :disabled="!locationId"
        @click="createLink"
      >
        Link aanmaken
      </v-btn>
    </div>

    <div v-else>
      <v-text-field
        variant="solo"
        :model-value="link"
        readonly
        append-inner-icon="mdi-content-copy"
        @click:append-inner="copy()"
        hide-details
      />
      <p class="text-neutral-600 mt-2">Geldig tot {{ expiresLabel }}</p>
      <k-spacing y="4"></k-spacing>
      <v-btn class="btn text-white" @click="copy" color="var(--tertiary)">
        Kopieer link
      </v-btn>
    </div>

    <v-snackbar v-model="snackbar" :color="snackbarColor">
      {{ snackbarText }}
    </v-snackbar>
  </v-dialog>
</template>

<script>
import api from "@/services/api";
import config from "@/config";

/*
 * Deelt de Specialistlink (ADR-0063).
 *
 * Bouwde voorheen zelf een URL: `${main}/link/${reference}?role=${constante}`.
 * Die route was ongeauthenticeerd, dekte de hele activiteit op alle vestigingen
 * en had geen vervaldatum -- en die rolconstante stond in de JS-bundle. De
 * server maakt de link nu aan, gescopet op activiteit + vestiging, met een
 * vervaldatum die uit de planning volgt.
 */
export default {
  props: {
    modelValue: Boolean,
    activityId: [Number, String],
  },
  emits: ["update:modelValue", "close-dialog"],

  data() {
    return {
      locations: [],
      locationId: null,
      loadingLocations: false,
      creating: false,
      link: "",
      expiresAt: null,
      snackbar: false,
      snackbarText: "",
      snackbarColor: "",
    };
  },

  computed: {
    expiresLabel() {
      if (!this.expiresAt) return "";
      return new Date(this.expiresAt * 1000).toLocaleDateString("nl-NL", {
        day: "numeric",
        month: "long",
        year: "numeric",
      });
    },
  },

  watch: {
    // Een nieuwe activiteit betekent een andere vestigingenlijst en een andere
    // link; alles wat van de vorige stond moet weg.
    modelValue: {
      immediate: true,
      handler(open) {
        if (!open) return;
        this.link = "";
        this.expiresAt = null;
        this.locationId = null;
        this.loadLocations();
      },
    },
    activityId() {
      this.link = "";
      this.locationId = null;
    },
  },

  methods: {
    loadLocations() {
      if (!this.activityId) return;
      this.loadingLocations = true;
      api
        .get(`${config.url.api}/v1/activities/${this.activityId}/locations`)
        .then((res) => {
          // Alleen vestigingen waar deze activiteit daadwerkelijk op staat, al
          // met een naam: precies de set die de server bij het maken van de link
          // toetst (`hasLocation`). Een locatie zonder toewijzing weigert hij met
          // "Deze activiteit draait niet op die locatie"; dat er nog geen datum
          // onder hangt mag, de link vervalt dan na 24 uur. Dit las eerst de hele
          // admin-boom, met elke aanmelding erin, voor deze ene keuzelijst.
          this.locations = res.data.success ?? [];

          if (this.locations.length === 1) {
            this.locationId = this.locations[0].id;
          }
        })
        .catch(() => {
          this.notify("Vestigingen ophalen mislukt.", "var(--primary)");
        })
        .finally(() => {
          this.loadingLocations = false;
        });
    },

    createLink() {
      this.creating = true;
      api
        .post(`${config.url.api}/v1/activities/${this.activityId}/share_link`, {
          location: this.locationId,
        })
        .then((res) => {
          this.link = res.data.url;
          this.expiresAt = res.data.expires_at;
          this.copy();
        })
        .catch((error) => {
          // De server zegt waaróm ("Kies een locatie voor deze link", "Deze
          // activiteit draait niet op die locatie"); dat is bruikbaarder dan een
          // algemene mislukking.
          this.notify(
            error?.response?.data?.message || "Link aanmaken mislukt.",
            "var(--primary)",
          );
        })
        .finally(() => {
          this.creating = false;
        });
    },

    async copy() {
      try {
        await navigator.clipboard.writeText(this.link);
        this.notify("Link gekopieerd.", "var(--secondary)");
      } catch (e) {
        this.notify("Fout bij het kopiëren van de link.", "var(--primary)");
      }
    },

    notify(text, color) {
      this.snackbarText = text;
      this.snackbarColor = color;
      this.snackbar = true;
    },
  },
};
</script>
